import React, { useState, useEffect } from 'react';
import { useRequisitVerificationsListQuery } from '@/entities/requisit/api'
import { BaseDialog, BaseTooltip } from '@/shared/components'
import { EnrichedPaymentRequisiteModel, PaymentRequisiteToType } from '@/entities/paymentRequisit'
import VerifyIcon from '@/shared/assets/icons/verify.svg?react'
import ClockTime from '@/shared/assets/icons/clockTime.svg?react'
import { formatCardNumber, formatPhoneNumber, RequisiteModel, useRequisiteOptions } from '@/features/requisites'

interface RequisitVerifyProps {
  paymentRequisit: EnrichedPaymentRequisiteModel,
  requesit: RequisiteModel
}

export const RequisitVerify: React.FC<RequisitVerifyProps> = ({
                                                                paymentRequisit,
                                                                requesit,
                                                              }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [timeLeft, setTimeLeft] = useState(15 * 60);

  const {
    requisitVerificationsData,
    requisitVerificationsIsLoading,
    refetch
  } = useRequisitVerificationsListQuery({
    requisitId: requesit.id, // Используем requisitId вместо paymentRequestId
  });

  // Находим профиль верификации по paymentRequestId
  const verificationProfile = requisitVerificationsData?.items?.find(
    item => item.paymentRequisitId === paymentRequisit.id
  );

  const { optionsBanks } = useRequisiteOptions(isModalOpen);
  const bankName =
    optionsBanks.find(b => b.value === requesit.bankId)?.label || 'Неизвестный банк';

  const isPhone = paymentRequisit?.requisitType === PaymentRequisiteToType.SbpPhoneNumber;
  const formattedRequisit = isPhone
    ? formatPhoneNumber(paymentRequisit?.value ?? '').formatted
    : formatCardNumber(paymentRequisit?.value ?? '').formatted;

  // Таймер для модального окна (15 минут)
  useEffect(() => {
    if (isModalOpen && timeLeft > 0) {
      const timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [isModalOpen, timeLeft]);

  // Автоматическое обновление каждые 30 секунд, если верификация не пройдена
  useEffect(() => {
    if (!verificationProfile?.isPassed && !requisitVerificationsIsLoading) {
      const refreshInterval = setInterval(() => {
        refetch();
      }, 30000); // 30 секунд

      return () => clearInterval(refreshInterval);
    }
  }, [verificationProfile?.isPassed, requisitVerificationsIsLoading, refetch]);

  const formatTime = (seconds: number) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m.toString().padStart(2, '0')} м ${s.toString().padStart(2, '0')} сек`;
  };

  const handleOpenModal = () => {
    setIsModalOpen(true);
    setTimeLeft(15 * 60);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleVerify = () => {
    console.log('Верификация подтверждена');
    // Принудительно обновляем данные после подтверждения
    refetch();
    handleCloseModal();
  };

  if (requisitVerificationsIsLoading) return <div>Загрузка...</div>;
  if (!verificationProfile) return null;
  if (verificationProfile.isPassed) return null;

  return (
    <>
      <button
        onClick={handleOpenModal}
        className="flex items-center justify-center gap-2 bg-[#FFF4D3] hover:bg-[#FFE8A8] text-[#DCA80C] focus:ring-2 focus:ring-[#FFD97A] focus:outline-none transition-colors duration-200 rounded-lg"
        style={{
          width: '171px',
          height: '24px',
          padding: '0 16px',
          cursor: 'pointer'
        }}
      >
        <VerifyIcon className="w-4 h-4" />
        <span className="text-sm font-medium text-gray-900">Верифицировать</span>
      </button>

      <BaseDialog
        isVisible={isModalOpen}
        onClose={handleCloseModal}
        title={`Переведите ${verificationProfile.requiredAmount} ₽ по ${isPhone ? 'номеру телефона' : 'номеру карты'}`}
        actionButtonName="Я оплатил"
        closeButtonName="Отменить верификацию"
        onAction={handleVerify}
        closeButton={true}
        xButton={true}
      >
        <div className="flex flex-col gap-3">
          <p className="text-sm text-[#667085]">
            Совершите платеж с другого банковского аккаунта.
            Так мы верифицируем данный реквизит
          </p>

          <div className="flex items-center bg-gray-100 dark:bg-gray-800 rounded-lg">
            <span className="flex gap-2 font-['Klein'] font-medium text-[14px] leading-[18px] text-[#5959FF] dark:text-[#5959FF]">
              <ClockTime />
              <span style={{marginTop:4}}>{formatTime(timeLeft)}</span>
            </span>
          </div>

          {/* Номер карты / телефона */}
          <div className="flex flex-col p-3 bg-gray-100 dark:bg-gray-800 rounded-lg gap-2">
            <span className="font-['Klein'] font-normal text-base leading-6 text-gray-500 dark:text-gray-400">
              {isPhone ? 'Номер телефона' : 'Номер карты'}
            </span>
            <span className="font-['Klein'] font-medium text-base leading-6 text-[#5959FF]">
              {formattedRequisit}
            </span>
          </div>

          {/* Сумма перевода */}
          <div className="flex flex-col p-3 bg-gray-100 dark:bg-gray-800 rounded-lg gap-2">
            <span className="font-['Klein'] font-normal text-base leading-6 text-gray-500 dark:text-gray-400">Сумма перевода</span>
            <span className="font-['Klein'] font-medium text-base leading-6 text-main-black dark:text-white">
              {verificationProfile.requiredAmount} ₽
            </span>
          </div>

          {/* ФИО */}
          <div className="flex flex-col p-3 bg-gray-100 dark:bg-gray-800 rounded-lg gap-2">
            <span className="font-['Klein'] font-normal text-base leading-6 text-gray-500 dark:text-gray-400">ФИО получателя</span>
            <span className="font-['Klein'] font-medium text-base leading-6 text-main-black dark:text-white">
              {requesit.personSurname} {requesit.personName} {requesit.personMiddlename}
            </span>
          </div>

          {/* Банк */}
          <div className="flex flex-col p-3 bg-gray-100 dark:bg-gray-800 rounded-lg gap-2">
            <span className="font-['Klein'] font-normal text-base leading-6 text-gray-500 dark:text-gray-400">Банк</span>
            <span className="font-['Klein'] font-medium text-base leading-6 text-main-black dark:text-white">
              {bankName}
            </span>
          </div>

          <div className="flex items-start gap-3 px-3 py-4 border rounded-lg">
            <BaseTooltip text="Подробнее" />
            <span className="text-text-secondary text-sm">
                Убедитесь, что правильно привязали реквизит. После 10 отказов реквизит будет
                остановлен.
              </span>
          </div>
          {timeLeft === 0 && (
            <p className="text-error text-sm">
              Время истекло. Пожалуйста, начните процесс верификации заново.
            </p>
          )}
        </div>
      </BaseDialog>
    </>
  );
};